import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import { toggleEditMode, updateWidgetProperty } from "actions/editor";
import { associateFontsWithWigdet } from "actions/fonts";

import _ from "lodash";
import cx from "classnames";
import { EditorContext } from "../../../../index";

import { Property } from "../../../../components/properties";
import { Widgets, Fonts } from "@paperflite-marketing/cleverstory-commons";
import { Font } from "../../../../components/properties/fonts";
import { Frame } from "scenejs";
import { FORM } from "constants/widgetTypes";
import { getColorsConfig } from "services/cssHelper.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";

import styles from "./checkBox.module.scss";

const FormCheckBoxWidget = React.memo(function CheckBoxEditor({
  className,
  style,
  widgetProps,
  widgetRef,
}) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const properties = _.get(widgetProps, "properties", {});

  Fonts.useFont(_.get(widgetProps, "fonts") || []);

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, "regular"),
  };

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.inputField)}
      style={widgetStyles}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      <div className={styles.container}>
        <Widgets.FormCheckBoxField
          className={styles.checkBox}
          widgetProps={widgetProps}
          style={{
            ...style,
            transform: `translate(0px, 0px) rotate(0deg) scale(1, 1)`,
          }}
          breakpoint={breakpoint}
        />
      </div>
    </div>
  );
});

const CheckBoxWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);
  };

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action onClick={handleEditMode.bind(this, "text")}>
        Edit Field
      </Property.Action>
      <Property.Divider />
      <Property.Action onClick={() => changeView("layout")}>
        <i className={"cleverstory-layout"} />
      </Property.Action>
      <Property.Action onClick={() => changeView("style")}>
        <i className={"cleverstory-customize"} />
      </Property.Action>

      {editMode && (
        <CheckBoxEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={id}
          editor={editorSession}
          breakpoint={"desktop"}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "style" && (
        <FieldDesignEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

const CheckBoxWidgetMobileAction = ({ editorSession, uid, ...rest }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === uid;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action onClick={() => changeView("layout")}>
        <FontAwesomeIcon icon={faSlidersH} />
      </Property.Action>

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={uid}
          editor={editorSession}
          breakpoint={"mobile"}
          widget={rest}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

FormCheckBoxWidget.moveableConfig = (editorSession, widget) => {
  const widgetElement = document.querySelector(
    `#${editorSession} .editor_widget_renderer #${widget.uid}`
  );
  const formElement = widgetElement?.closest(`div[data-widget-type="${FORM}"]`);
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = formElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const fieldName = _.get(widget, `properties.desktop.field`);
  const deletable = fieldName != "email";

  const bounds = {
    left: left,
    right: left + rect?.width,
    top: top,
    bottom: top + rect?.height,
  };

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: false,
    deletable: deletable,
    renderDirections: ["e", "w"],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false;
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set("height", "auto");
      return frame.get();
    },
  };
};

FormCheckBoxWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === "desktop" && (
        <CheckBoxWidgetDesktopAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}

      {breakpoint === "mobile" && (
        <CheckBoxWidgetMobileAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}
    </>
  );
};

/**
 * Field input editor
 *
 */
const CheckBoxEditor = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "widget.uid");
  const mandatory =
    _.get(props, "widget.properties.desktop.mandatory") || false;

  const handleMandatoryChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, "desktop", {
        mandatory: value,
      })
    );
  };

  return (
    <Property.Dialog
      width={222}
      target={`#${id}`}
      title="Field Settings"
      onCancel={props.onCancel}
    >
      <Property>
        <Property.Switch
          id="required"
          title={"This field is required"}
          value={mandatory}
          onChange={() => handleMandatoryChange(!mandatory)}
        />
      </Property>
    </Property.Dialog>
  );
};

/**
 * Alignment Editor
 *
 */
const AlignmentEditor = ({ id, widget, breakpoint, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const fieldSpacing =
    _.get(widget, `properties.${breakpoint}.styles.layout.fieldSpacing`) || 0;

    const inputPadding =
    _.get(widget, `properties.${breakpoint}.styles.layout.inputPadding`) || 0;

  const handlePropertyChange = (field, value) => {
    var request = _.get(widget, `properties.${breakpoint}.styles`) || {};

    request = _.cloneDeep(request);
    _.set(request, `layout.${field}`, value);
    handleChange(request);
  };

  const handleChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  return (
    <Property.Dialog
      className={styles.fieldLayoutDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Layout"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property
          name="Space between checkBox &amp; Instruction"
          className={styles.marginProperty}
        >
          <Property.Slider
            min={0}
            max={100}
            step={1}
            input={true}
            value={inputPadding}
            onChange={(value) =>
              handlePropertyChange("inputPadding", value)
            }
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
};

/**
 * Field design editor
 *
 */
const FieldDesignEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const fieldStyles = _.get(properties, `styles.regular`, {});

  const labelStyles = _.get(fieldStyles, "label");
  const inputStyles = _.get(fieldStyles, "input");

  const color = _.get(inputStyles, "color");
  const placeholderColor = _.get(inputStyles, "placeholderColor");
  const fontFamily = _.get(inputStyles, "font.fontFamily");
  const fontSize = _.get(inputStyles, "font.fontSize") || 14;

  const handlePropertyChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  const handleInputStyleChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.input`, value);
    handlePropertyChange(request);
  };

  const handleCheckBoxColorChange = (color) => {
    var request = _.cloneDeep(inputStyles);
    _.set(request, "color", color);
    handleInputStyleChange(request);
  };

  const handleColorChange = (color) => {
    var request = _.cloneDeep(inputStyles);
    _.set(request, "placeholderColor", color);
    handleInputStyleChange(request);
  };

  const handleFontFamilyChange = (font) => {
    var fontId = _.get(font, "font.id");
    var familyValue = _.get(font, "font.fontFamily");
    var request = _.cloneDeep(inputStyles);
    _.set(request, "font.fontFamily", familyValue);
    handleInputStyleChange(request);

    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [fontId]));
    }
  };

  const handleFontSizeChange = (size) => {
    var request = _.cloneDeep(inputStyles);
    _.set(request, "font.fontSize", size);
    handleInputStyleChange(request);
  };

  return (
    <Property.Dialog
      className={styles.fieldDesignDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Design"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <div className={styles.fieldDesignProperty}>
            <Property.Color
              title={"Label Color"}
              value={placeholderColor}
              onChange={handleColorChange}
            />

            <Font.Family
              value={fontFamily}
              onChange={handleFontFamilyChange}
              className={styles.inputTextStyleProperty}
            />
            <Font.Size
              value={fontSize}
              onChange={handleFontSizeChange}
              className={styles.inputTextStyleProperty}
            />

            <Property.Color
              title={"Box Color"}
              clearable={false}
              value={color}
              onChange={handleCheckBoxColorChange}
            />
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

/**
 * --------------------------------------------------
 * Field Styles
 * --------------------------------------------------
 */
function getFieldStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const activeStyles = _.get(defaultProperties, `${active}`, {});

  //alignment
  const alignment =
    _.get(deviceProperties, "styles.layout.alignment") || "left";
  styles["alignItems"] = alignment;

  if (alignment === "left") {
    styles["alignItems"] = "flex-start";
  } else if (alignment === "right") {
    styles["alignItems"] = "flex-end";
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = "inherit";
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color,
  };

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = _.get(border, "radius.topLeft", null);
  const topRight = _.get(border, "radius.topRight", null);
  const bottomLeft = _.get(border, "radius.bottomLeft", null);
  const bottomRight = _.get(border, "radius.bottomRight", null);

  if (topLeft) {
    styles["borderTopLeftRadius"] = parseInt(topLeft) + "px";
  }

  if (topRight) {
    styles["borderTopRightRadius"] = parseInt(topRight) + "px";
  }

  if (bottomLeft) {
    styles["borderBottomLeftRadius"] = parseInt(bottomLeft) + "px";
  }

  if (bottomRight) {
    styles["borderBottomRightRadius"] = parseInt(bottomRight) + "px";
  }

  const borderStyle = _.get(border, "style") || "bottom";
  if (borderStyle !== "none") {
    const borderWidthString = _.get(border, "width") || 0;
    var borderWidget = parseInt(borderWidthString) + "px";

    const fieldName =
      borderStyle == "all" ? "borderWidth" : "borderBottomWidth";
    styles[fieldName] = borderWidget;

    const colorRgb = _.get(border, "color");
    var color = "transparent";
    if (colorRgb) {
      color = getColorsConfig(colorRgb);
    }

    styles["borderColor"] = color;
  }

  return styles;
}

export function getFontStyle(fontStyles) {
  var styles = {};

  if (!fontStyles) {
    return styles;
  }

  //background-color
  const backgroundColorRgb = _.get(fontStyles, "background", null);
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb),
    };
  }

  //font-color
  const colorRgb = _.get(fontStyles, "color", null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color,
  };

  //font-family
  const fontFamily = _.get(fontStyles, "font.fontFamily", "inherit");
  styles = {
    ...styles,
    fontFamily: fontFamily,
  };

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14;
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + "px";
    styles["fontSize"] = fontSize;
  }

  return styles;
}

FormCheckBoxWidget.processRequest = (widget) => {
  if(_.get(widget, 'content.id')){
    widget = {
      ...widget,
      content: _.get(widget, 'content.id')
    }
  }
  return widget;
};

export { FormCheckBoxWidget };
